// Diagnostics attached to guard-exhausted lazy-chunk failures. Every function here is
// called from an error path, so none of them may ever throw: DOM/Performance reads are
// wrapped and degrade to an empty/"unknown" value.

const MAX_SUSPECT_ENTRIES = 5;
// log.ts truncates every extra string to 500 chars; stay under that so the JSON is never cut.
const MAX_ASSETS_LENGTH = 480;
const MAX_PATH_LENGTH = 120;
const DEPLOY_CHECK_TIMEOUT_MS = 3000;
const SCRIPT_TAG_RE = /<script\b[^>]*>/gi;
const MODULE_TYPE_RE = /\btype\s*=\s*["']module["']/i;
const ENTRY_SRC_RE = /\bsrc\s*=\s*["']([^"']*\/assets\/index-[\w-]+\.js)["']/i;

export function describeModule(m: unknown): { resolved: string; keys: string; defaultType: string } {
  if (m === undefined) {
    return { resolved: 'undefined', keys: '', defaultType: 'n/a' };
  }
  if (m === null) {
    return { resolved: 'null', keys: '', defaultType: 'n/a' };
  }
  if (typeof m !== 'object') {
    return { resolved: typeof m, keys: '', defaultType: 'n/a' };
  }
  try {
    return {
      resolved: 'object',
      keys: Object.keys(m).slice(0, 5).join(','),
      defaultType: typeof (m as { default?: unknown }).default,
    };
  } catch {
    return { resolved: 'object', keys: '', defaultType: 'n/a' };
  }
}

type ResourceEntry = Partial<PerformanceResourceTiming> & { contentType?: string };

function looksWrong(e: ResourceEntry): boolean {
  const status = e.responseStatus;
  if (typeof status === 'number' && status >= 400) {
    return true;
  }
  if (status === 200 && e.decodedBodySize === 0) {
    return true;
  }
  const contentType = e.contentType;
  return typeof contentType === 'string' && contentType !== '' && !contentType.includes('javascript');
}

function isAssetJs(name: unknown): name is string {
  if (typeof name !== 'string' || !name.includes('/assets/')) {
    return false;
  }
  const path = name.split(/[?#]/, 1)[0] ?? '';
  return path.endsWith('.js');
}

function pathAfterOrigin(name: string): string {
  try {
    return new URL(name).pathname;
  } catch {
    return name;
  }
}

// JS asset requests the browser recorded that look broken (HTTP error, empty 200, or a
// non-JS content type). Missing fields are "no signal", never a flag.
export function suspectAssetEntries(): string {
  try {
    const entries = performance.getEntriesByType?.('resource') as ResourceEntry[] | undefined;
    if (!entries) {
      return '';
    }
    const suspects = entries
      .filter((e) => isAssetJs(e.name) && looksWrong(e))
      .slice(0, MAX_SUSPECT_ENTRIES)
      .map((e) => {
        const item: Record<string, string | number> = { n: pathAfterOrigin(e.name as string).slice(-MAX_PATH_LENGTH) };
        const fields: [string, string | number | undefined][] = [
          ['s', e.responseStatus],
          ['t', e.transferSize],
          ['e', e.encodedBodySize],
          ['d', e.decodedBodySize],
          ['c', e.contentType],
        ];
        for (const [k, v] of fields) {
          if (v !== undefined) {
            item[k] = v;
          }
        }
        return item;
      });
    // Add whole entries until the next one would push the serialized array past the limit;
    // never slice the serialized string, so the result always parses.
    const kept: Record<string, string | number>[] = [];
    for (const item of suspects) {
      if (JSON.stringify([...kept, item]).length > MAX_ASSETS_LENGTH) {
        break;
      }
      kept.push(item);
    }
    return kept.length === 0 ? '' : JSON.stringify(kept);
  } catch {
    return '';
  }
}

export function pageContext(): {
  online: boolean | undefined;
  visibility: string | undefined;
  hasRefreshParam: boolean;
} {
  let online: boolean | undefined;
  let visibility: string | undefined;
  let hasRefreshParam = false;
  try {
    online = navigator.onLine;
  } catch {
    // leave undefined
  }
  try {
    visibility = document.visibilityState;
  } catch {
    // leave undefined
  }
  try {
    hasRefreshParam = new URLSearchParams(window.location.search).has('refresh');
  } catch {
    // leave false
  }
  return { online, visibility, hasRefreshParam };
}

function runningEntryPathname(): string | undefined {
  const src = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.src;
  if (!src) {
    return undefined;
  }
  return pathAfterOrigin(src);
}

function findEntryPathname(html: string): string | undefined {
  // Only a <script type="module" src=".../assets/index-*.js"> tag counts; a modulepreload
  // <link> or any other mention of an index-*.js chunk does not. Attribute order is free.
  for (const tag of html.match(SCRIPT_TAG_RE) ?? []) {
    if (MODULE_TYPE_RE.test(tag)) {
      const src = ENTRY_SRC_RE.exec(tag)?.[1];
      if (src) {
        return pathAfterOrigin(new URL(src, window.location.origin).href);
      }
    }
  }
  return undefined;
}

async function compareEntry(
  running: string,
  signal: AbortSignal | undefined
): Promise<'deployed' | 'same-build' | 'unknown'> {
  const response = await fetch('/index.html', { cache: 'no-store', signal });
  if (!response.ok) {
    return 'unknown';
  }
  const latest = findEntryPathname(await response.text());
  if (!latest) {
    return 'unknown';
  }
  return latest === running ? 'same-build' : 'deployed';
}

// Compares the entry script this page booted from against the one the server currently
// serves in /index.html. A different entry means a deploy happened; the same entry means
// the failure is NOT deploy skew. The whole check is raced against a timer, independent of
// AbortSignal support, so a stalled fetch/body read can never delay the caller's redirect.
export async function detectDeploy(): Promise<'deployed' | 'same-build' | 'unknown'> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abortTimer: ReturnType<typeof setTimeout> | undefined;
  try {
    const running = runningEntryPathname();
    if (!running) {
      return 'unknown';
    }
    let signal: AbortSignal | undefined;
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      signal = controller.signal;
      abortTimer = setTimeout(() => controller.abort(), DEPLOY_CHECK_TIMEOUT_MS);
    }
    const timeout = new Promise<'unknown'>((resolve) => {
      timer = setTimeout(() => resolve('unknown'), DEPLOY_CHECK_TIMEOUT_MS);
    });
    return await Promise.race([compareEntry(running, signal), timeout]);
  } catch {
    return 'unknown';
  } finally {
    clearTimeout(timer);
    clearTimeout(abortTimer);
  }
}
